import { logError } from 'App/services/faroService';
import { IDP_GOOGLE_OAUTH_CALLBACK } from 'Pages/help/constants';
import { SSOButtonOptionsByProvider, SSOProvider } from 'Shared/components/design/SSOButton/constants';
import { navigateToMemberPage } from 'Shared/services/routingService';
import { postAppleSignIn, postFacebookSignIn, postGoogleSignIn } from 'Shared/services/userActivation/api/idpApiService';

/**
 * Initializes the Google SSO code client
 */
export const initializeGoogleSSO = (errorCallback?: (x: SSOProvider) => void, isLoadingCallback?: (isLoading: boolean) => void): Window["google"]["accounts"]["oauth2"]["CodeClient"] | undefined => {
  if (window.google?.accounts?.oauth2) {
    return window.google.accounts.oauth2.initCodeClient({
    client_id   : process.env.PUBLIC_GOOGLE_SSO_CLIENT_ID || '',
      scope       : 'openid email profile',
      ux_mode     : 'popup',
      redirect_uri: `${process.env.REACT_APP_IDP_BASE_URL}${IDP_GOOGLE_OAUTH_CALLBACK}`,
      callback    : (tokenResponse: { code?: string; error?: string }) => {
        isLoadingCallback?.(true);

        if (tokenResponse.error) {
          errorCallback?.(SSOProvider.GOOGLE);
          isLoadingCallback?.(false);
          return;
        }

        if (tokenResponse.code) {
          postGoogleSignIn(tokenResponse.code).then(() => {
            navigateToMemberPage();
          }).catch(() => {
            errorCallback?.(SSOProvider.GOOGLE);
          }).finally(() => {
            isLoadingCallback?.(false);
          });
          return;
        }

        isLoadingCallback?.(false);
        errorCallback?.(SSOProvider.GOOGLE);
      },
    });
  }
};

/**
 * Wait for Google SDK to be fully available after script loads
 */
const waitForGoogleSDK = (maxAttempts = 10, delayMs = 100): Promise<void> => {
  return new Promise((resolve, reject) => {
    let attempts = 0;

    const checkSDK = () => {
      if (window.google?.accounts?.oauth2) {
        resolve();
        return;
      }

      attempts++;
      if (attempts >= maxAttempts) {
        reject(new Error('Google SDK not available after loading'));
        return;
      }

      setTimeout(checkSDK, delayMs);
    };
    checkSDK();
  });
};

/**
 * Load Google OAuth Client Script
 * @returns Promise<void>
 */
export const loadGoogleSDK = (): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    // Check if script exists AND is successfully loaded (has data-loaded attribute)
    const existingScript = document.getElementById('google-oauth-client');
    if (existingScript && existingScript.hasAttribute('data-loaded')) {
      resolve();
      return;
    }

    // Remove any existing failed script
    if (existingScript) {
      existingScript.remove();
    }

    const script       = document.createElement('script');
          script.id    = 'google-oauth-client';
          script.src   = 'https://accounts.google.com/gsi/client';
          script.async = true;
          script.defer = true;

    // resolve promise on load
    script.onload = () => {
      // Mark script as successfully loaded
      script.setAttribute('data-loaded', 'true');

      // wait for Google SDK to initialize its global objects
      waitForGoogleSDK()
        .then(() => {
          resolve();
        })
        .catch((err) => {
          // Remove failed script so it can be retried
          script.remove();
          reject(err);
        });
    };

    // reject promise on error
    script.onerror = (err) => {
      // Remove failed script so it can be retried
      script.remove();
      reject(err);
    };

    document.body.appendChild(script);
  });
};

/**
 * Load Apple OAuth Client Script
 * @returns Promise<void>
 */
export const loadAppleSDK = (): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    if (document.getElementById('apple-oauth-client')) {
      resolve();
      return;
    }

    const script       = document.createElement('script');
          script.id    = 'apple-oauth-client';
          script.src   = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
          script.async = true;
          script.defer = true;

    // resolve promise on load
    script.onload = () => {
      // Check if AppleID is available after loading
      if (window.AppleID?.auth) {
        resolve();
      } else {
        reject(new Error('AppleID.auth not available after script load'));
      }
    };

    // reject promise on error
    script.onerror = (err) => {
      reject(err);
    };

    document.body.appendChild(script);
  });
};

/**
 * Load Facebook SDK
 * @returns Promise<void>
 */
export const loadFacebookSDK = (): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    // check if the script is already injected, if so, resolve
    if (document.getElementById('facebook-jssdk')) {
      resolve();
      return;
    }

    // set up the global callback that Facebook calls once it's loaded
    window.fbAsyncInit = function () {
      window.FB.init({
        appId  : process.env.PUBLIC_FACEBOOK_APP_ID || '',
        cookie : true,
        xfbml  : false,
        version: 'v17.0',
      });

      resolve();
    };

    // create the script element
    const script       = document.createElement('script');
          script.id    = 'facebook-jssdk';
          script.src   = 'https://connect.facebook.net/en_US/sdk.js';
          script.async = true;
          script.defer = true;

    // if there's a network error loading the script, reject
    script.onerror = (err) => {
      reject(err);
    };

    document.body.appendChild(script);
  });
};

/**
 * Apple Sign-In response type
 */
type AppleAuthResponse = {
  authorization?: {
    code?: string;
    id_token?: string;
  };
  user?: unknown;
  error?: string;
};

/**
 * Initializes the Apple SSO client
 */
export const initializeAppleSSO = (): void => {
  const clientId = process.env.PUBLIC_APPLE_CLIENT_ID;
  const redirectURI = process.env.IDP_APPLE_AUTH_REDIRECT_URI;

  if (window.AppleID?.auth) {
    try {
      // Initialize Apple SDK with code id_token response type
      window.AppleID.auth.init({
        clientId: clientId || '',
        scope: 'name email',
        redirectURI: redirectURI || '',
        state: 'apple-signin',
        usePopup: true,
        response_type: 'code id_token',
        response_mode: 'fragment',
      });
    } catch {
      // Do nothing
    }
  }
};

/**
 * Handles Apple Login (pop-up) using promise-based API.
 * - Sends id_token to backend if successful
 * - Ignores popup closes or user cancellations silently
 * - Calls errorCallback only for real errors
 */
export const handleAppleLogin = async (
  errorCallback?: (x: SSOProvider) => void,
  isLoadingCallback?: (isLoading: boolean) => void
) => {
  const AppleAuth = (window as any).AppleID?.auth;
  if (!AppleAuth || typeof AppleAuth.signIn !== 'function') {
    errorCallback?.(SSOProvider.APPLE);
    return;
  }

  // Helper to detect "safe to ignore" user actions (cancel/close)
  const isUserClosedPopup = (input?: unknown): boolean => {
    const msg = (
      (typeof input === 'string'
        ? input
        : (input as any)?.error ||
          (input as any)?.message ||
          (input as any)?.code ||
          '')
    )
      .toString()
      .toLowerCase();

    const cancelIndicators = ['cancel', 'popup', 'abort', 'user', 'notallowed', 'close'];
    return cancelIndicators.some((k) => msg.includes(k));
  };

  try {
    isLoadingCallback?.(true);
    const res = (await AppleAuth.signIn()) as AppleAuthResponse;

    // Handle Apple returning an error payload directly
    if (res?.error) {
      if (isUserClosedPopup(res.error)) {
        // User closed or cancelled the popup manually — silent exit
        return;
      }

      errorCallback?.(SSOProvider.APPLE);
      return;
    }

    //handle successful sign-in
    const idToken = res?.authorization?.id_token;
    if (!idToken) {
      logError('Apple SSO: No id_token from Apple', { response: res });
      errorCallback?.(SSOProvider.APPLE);
      return;
    }

    await postAppleSignIn(idToken);
    navigateToMemberPage();
  } catch (e) {
    // handle thrown/rejected errors (popup closed, network, etc.)
    if (isUserClosedPopup(e)) {
      return;
    }

    logError('Apple SSO: Sign-in failed', { error: e });
    errorCallback?.(SSOProvider.APPLE);
  } finally {
    isLoadingCallback?.(false);
  }
};



/**
 * Handles Facebook oauth response (FB.login()) and passes it to IDP API
 */
export const handleFacebookResponse = (response: { status: string; authResponse?: { accessToken: string } }, errorCallback?: (x: SSOProvider) => void, isLoadingCallback?: (isLoading: boolean) => void): void => {
  // if the user is 'connected', we have a valid FB authResponse
  if (response.status === 'connected' && response.authResponse?.accessToken) {
    isLoadingCallback?.(true);
    const token = response.authResponse.accessToken;
    postFacebookSignIn(token)
      .then(() => {
        navigateToMemberPage();
      })
      .catch(() => {
        errorCallback?.(SSOProvider.FACEBOOK);
      }).finally(() => {
        isLoadingCallback?.(false);
      });
  }
};

/**
 * Initiate Facebook Login (pop-up).
 */
export const handleFacebookLogin = (errorCallback?: (x: SSOProvider) => void, isLoadingCallback?: (isLoading: boolean) => void) => {
  if (!window.FB) {
    errorCallback?.(SSOProvider.FACEBOOK);
    return;
  }

  // trigger Facebook login popup and handle the response
  window.FB.login((response) => handleFacebookResponse(response, errorCallback, isLoadingCallback), { scope: 'email, public_profile' });
};

/**
 * Determines the SSO provider name
 * @param provider
 * @returns
 */
export const getSSOProviderName = (ssoProvider: SSOProvider): string => {
  if (ssoProvider === SSOProvider.GOOGLE) {
    return SSOButtonOptionsByProvider[SSOProvider.GOOGLE].name;
  }
  if (ssoProvider === SSOProvider.FACEBOOK) {
    return SSOButtonOptionsByProvider[SSOProvider.FACEBOOK].name;
  }
  if (ssoProvider === SSOProvider.APPLE) {
    return SSOButtonOptionsByProvider[SSOProvider.APPLE].name;
  }

  return '';
};
